'use strict';

import {
	Frustum,
	Matrix4,
	MOUSE,
	Object3D,
	TOUCH,
	Vector3,
	MathUtils,
	TextureLoader,
	NearestFilter,
	Vector2,
	LinearFilter,
	Clock
} from 'three';
import { TilesSide } from './components/Side';
import { OrbitControls } from '../../../libs/OrbitControls';
import { BuildingsPlugin } from './plugins/BuildingsPlugin';
import { Cloudflare } from '../../api/Cloudflare';
import { isMobile, resourcesFolder } from '../../../settings';
import { BuildingsHomeLabelPlugin } from './plugins/BuildingsHomeLabelPlugin';
import { PanoramaToMapPlugin } from './plugins/PanoramaToMapPlugin';
import { autoRotateTimerAction } from '../../controls/orbitControls';
import { PanoramaCompassPlugin } from './plugins/PanoramaCompassPlugin';
import { BuildingLabelsPlugin } from './plugins/BuildingLabelsPlugin';


const FACES = ['px','nx','py','ny','pz','nz'];
const _matrix = new Matrix4();
const _frustum = new Frustum();
const textureLoader = new TextureLoader();

const EARTH_RADIUS = 6378137;
const DEFAULT_CONFIG = {
	"size": 500,
	"maxLevel": 0,
	"fov": {
		"dampingFactor": 0.4,
		"min": 0.0055,
		"current": 80,
		"max": 130
	},
	"controls": {
		"rotateSpeed": 0.25,
		"minPolarAngle": 0,
		"maxPolarAngle": 180,
		"dampingFactor": 0.08
	},
	"tilesUrl": "",
	"nightTilesUrl": "",
	"panorama": {
		"position": [0,0,0],
		"rotation": [0,0,0],
		"target": [0,0,-0.00001],
		"center": {
			"lat": 0,
			"lng": 0,
			"altitude": 0
		}
	},
	"masks": {},
	"plugins": [],
	"mapping":[]
};

export class TilesPanoramaViewer extends Object3D{
	constructor (camera,renderer,config) {
		super();
		
		this.enabled = false;
		this.camera = camera;
		this.renderer = renderer;
		this.container = renderer.domElement;
		this.config = config ? config : DEFAULT_CONFIG;
		this.panoramaId = null;
		
		this.controls = new OrbitControls(this.camera,this.container);
		this.controls.addEventListener('change',this.updateTiles.bind(this));
		
		this.levels = {};
		this.sides = [];
		this.tiles = [];
		this.alpha = 0;
		this.level = 0;
		this.delta = 0;
		this.clock = new Clock();
		this.useTime = false;
		this.started = false;
		this.mask = {};
		this.plugins = [];
		this.pointer = {
			down: new Vector2(),
			up: new Vector2(),
			distance: 0
		};
		
		this.controls.mouseButtons = {
			LEFT: MOUSE.ROTATE,
			MIDDLE: null,
			RIGHT: null
		};
		
		this.controls.touches = {
			ONE: TOUCH.ROTATE,
			TWO: null
		};
		
		this.controls.keys = {
			LEFT: 37,
			UP: 38,
			RIGHT: 39,
			BOTTOM: 40
		};
		
		this.controls.enabled = false;
		this.controls.rotationAndPan = false;
		this.controls.screenSpacePanning = false;
		this.controls.enablePan = false;
		this.controls.enableZoom = false;
		this.controls.enableSetAngle = false;
		this.controls.minPolarAngle = MathUtils.degToRad(this.config.controls.minPolarAngle);
		this.controls.maxPolarAngle = MathUtils.degToRad(this.config.controls.maxPolarAngle);
		this.controls.enableDamping = true;
		this.controls.dampingFactor = this.config.controls.dampingFactor;
		this.controls.rotateSpeed = this.config.controls.rotateSpeed;
		this.controls.reverseControls = true;
		
		this.controls.target.fromArray(this.config.panorama.position);
		this.position.fromArray(this.config.panorama.position);
		this.rotation.fromArray(this.config.panorama.rotation);
		this.updateMatrixWorld();
		
		this.isLOD = true;
		this.autoUpdate = true;
		this.name = 'TilesPanoramaViewer';
		
		this.onWheel = this.#onWheel.bind(this);
		this.onTouchStart = this.#onTouchStart.bind(this);
		this.onTouchMove = this.#onTouchMove.bind(this);
		
		this.container.addEventListener('wheel',this.onWheel);
		this.container.addEventListener( 'touchstart', this.onTouchStart);
		this.container.addEventListener( 'touchmove', this.onTouchMove);
	}
	
	#onTouchStart({touches}) {
		if (!this.enabled)
			return;
		
		if (touches.length === 2) {
			this.pointer.distance = Math.hypot(touches[0].pageX - touches[1].pageX, touches[0].pageY - touches[1].pageY);
		}
	}
	
	#onTouchMove({touches}) {
		if (!this.enabled)
			return;
		
		if (touches.length === 2) {
			const dist = Math.hypot(touches[0].pageX - touches[1].pageX, touches[0].pageY - touches[1].pageY);
			const diff = this.pointer.distance - dist;
			if (Math.abs(diff) > 25) {
				this.pointer.distance = dist;
				this.#zoom(
					this.camera.fov,
					MathUtils.clamp(this.camera.fov + diff * this.camera.fov / 250, this.config.fov.min, this.config.fov.max)
				);
			}
		}
	}
	
	#onWheel(e) {
		if (!this.enabled)
			return;
		
		this.#zoom(
			this.camera.fov,
			MathUtils.clamp(this.camera.fov + e.deltaY * this.camera.fov / 1000, this.config.fov.min, this.config.fov.max)
			)
	}
	
	start() {
		this.camera.fov = this.config.fov.current;
		this.camera.updateProjectionMatrix();
		
		this.camera.position.copy(this.controls.target).add(new Vector3().fromArray(this.config.panorama.target));
		this.camera.lookAt(this.controls.target);
		this.controls.saveState();
		this.controls.enabled = true;
		this.enabled = true;
		this.controls.rotateSpeed = this.config.controls.rotateSpeed * (this.camera.getEffectiveFOV() / 90);
		
		this.loadMasks();
		this.load(0);
		this.load(1);
		this.registerPlugins();
		this.#updateLevel();
		this.started = true;
		
		return this;
	}
	
	disable() {
		this.enabled = false;
		this.controls.enabled = false;
		
		for (let i = 0; i < this.tiles.length; i++) {
			if (this.tiles[i].level > 1) {
				this.tiles[i].dispose();
			}
		}
		
		this.disablePlugins();
	}
	
	enable() {
		this.enabled = true;
		this.controls.enabled = true;
		this.controls.reset();
		this.camera.fov = this.config.fov.current;
		this.camera.updateProjectionMatrix();
		this.controls.rotateSpeed = this.config.controls.rotateSpeed * (this.camera.getEffectiveFOV() / 90);
		
		this.#updateLevel();
		this.updateTiles();
	}
	
	enablePlugins() {
		for (let i = 0; i < this.plugins.length; i++) {
			this.plugins[i].enable();
		}
	}
	
	disablePlugins() {
		for (let i = 0; i < this.plugins.length; i++) {
			this.plugins[i].disable();
		}
	}
	
	dispose() {
		this.disable();
		this.removeFromParent();
		
		for (let level in this.levels) {
			for (let side of this.levels[level]) {
				side.dispose();
			}
		}
		
		for (let key in this.mask) {
			this.mask[key].dispose();
		}
		
		for (let i = 0; i < this.plugins.length; i++) {
			this.plugins[i].dispose();
		}
		
		this.container.removeEventListener('wheel',this.onWheel);
		this.container.removeEventListener( 'touchstart', this.onTouchStart);
		this.container.removeEventListener( 'touchmove', this.onTouchMove);
		
		this.onWheel = null;
		this.onTouchStart = null;
		this.onTouchMove = null;
		
		this.controls.dispose();
		this.controls = null;
		this.levels = {};
		this.sides = [];
		this.tiles = [];
		this.level = 0;
		this.mask = {};
	}
	
	init(textureLoader) {
		this.loadMasks(textureLoader);
		this.load(0,textureLoader);
	}
	
	loadMasks(tl) {
		if (Object.keys(this.mask).length)
			return;
		
		const origin = `${config.cdnRepository}/${config.projectFolder}/${resourcesFolder}/textures/air`;
		
		Object.entries(this.config.masks).forEach(([key,url]) => {
			if (isMobile && key !== 'buildings')
				return;
			
			if (key === 'trees') {
				this.useTime = true;
			}
			
			const imageUrl = Cloudflare.resize(origin + url,{
				width: isMobile ? 2048 : 4096
			});
			this.mask[key] = tl ? tl.load(imageUrl) : textureLoader.load(imageUrl);
			this.mask[key].magFilter = LinearFilter;
			this.mask[key].minFilter = NearestFilter;
			this.mask[key].anisotropy = 4;
			this.mask[key].needsUpdate = true;
		});
	}
	
	registerPlugins() {
		this.config.plugins.forEach(plugin => {
			let scope = null;
			
			switch (plugin) {
				case 'BuildingsPlugin':
					scope = new BuildingsPlugin(this);
					break;
				case 'BuildingsHomeLabelPlugin':
					scope = new BuildingsHomeLabelPlugin(this);
					break;
				case 'PanoramaToMapPlugin':
					scope = new PanoramaToMapPlugin(this);
					break;
				case 'PanoramaCompassPlugin':
					scope = new PanoramaCompassPlugin(this);
					break;
				case 'BuildingLabelsPlugin':
					scope = new BuildingLabelsPlugin(this);
					break;
				
			}
			
			if (scope) {
				this.plugins.push(scope);
			}
		});
	}
	
	getPlugin(type) {
		return this.plugins.find(plugin => plugin.type === type);
	}
	
	#applyMasks(side) {
		Object.entries(this.mask).forEach(([key,texture]) => {
			side.setTexture(key,texture);
		});
	}
	
	load(level,tl) {
		if (level > this.config.maxLevel)
			return;
		
		if (!this.levels.hasOwnProperty(level)) {
			this.levels[level] = [];
			FACES.forEach(face => {
				const side = new TilesSide(face,level,this.config);
				this.#applyMasks(side);
				side.setPanoMatrix(this.matrixWorld);
				this.tiles.push(...side.tiles);
				
				if (level === 0) {
					side.load(tl);
				}
				
				this.levels[level].push(side);
				this.sides.push(side);
				this.add(side);
			});
		}
	}
	
	#zoom(from,to) {
		let currentFov = from;
		const targetFov =  to;
		
		const smoothStep = () => {
			currentFov += (targetFov - currentFov) * this.config.fov.dampingFactor;
			this.camera.fov = currentFov;
			this.camera.updateProjectionMatrix();
			this.controls.rotateSpeed = this.config.controls.rotateSpeed * (this.camera.getEffectiveFOV() / 90);

			if (Math.abs(targetFov - currentFov) > 0.01) {
				requestAnimationFrame(smoothStep);
			} else {
				this.#updateLevel();
				this.updateTiles();
			}
		};

		smoothStep();
	}
	
	#updateLevel() {
		if (!this.enabled)
			return;
		
		this.level = this.#calculateRequiredLevel();
		this.load(this.level);
	}
	
	#calculateRequiredLevel() {
		const distance = this.config.size / 4;
		const viewHeight = 2 * Math.tan((this.camera.fov / 2) * (Math.PI / 180)) * distance;
		let level = Math.max(1, Math.ceil(Math.log2(this.config.size / viewHeight)));
		level = MathUtils.clamp(level,0,this.config.maxLevel);
		
		return level;
	}
	
	updateAlpha() {
		if (this.config.nightTilesUrl.length === 0)
			return;
		
		for (let i = 0; i < this.tiles.length; i++) {
			this.tiles[i].updateAlpha(this.alpha);
		}
	}
	
	updateTiles() {
		autoRotateTimerAction('update');
		_matrix.multiplyMatrices(this.camera.projectionMatrix,this.camera.matrixWorldInverse);
		_frustum.setFromProjectionMatrix(_matrix);
		
		for (let i = 0; i < this.tiles.length; i++) {
			const tile = this.tiles[i];
			if (tile.inView(_frustum)) {
				tile.updateAlpha(this.alpha);

				if (tile.level === this.level || tile.level === 1) {
					tile.load();
				} else if (tile.level > 1 && tile.level > this.level) {
					tile.dispose();
				}

			} else if (tile.level > 1) {
				tile.dispose();
			}
		}
		
		for (let i = 0; i < this.plugins.length; i++) {
			this.plugins[i].update(_frustum);
		}
	}
	
	#updateDelta(delta) {
		this.delta += delta;
		
		for (let i = 0; i < this.tiles.length; i++) {
			if (this.tiles[i].loaded) {
				this.tiles[i].updateDelta(this.delta);
			}
		}
	}
	
	update() {
		if (!this.enabled)
			return;

		this.controls.update();
		
		if (this.useTime) {
			this.#updateDelta(this.clock.getDelta());
		}
		
	}
	
	project(data) {
		const cartesian = convertToSphereLocalCoordinates(this.config.panorama.center,data,this.config.size);
		cartesian.multiply(new Vector3(-1,1,1)).applyAxisAngle(new Vector3(0,1,0),Math.PI).add(this.position);
		
		return cartesian;
	}
	
	resize() {
		for (let i = 0; i < this.plugins.length; i++) {
			this.plugins[i].resize();
		}
	}
	
}


function convertToSphereLocalCoordinates (origin,target, radius) {
	
	const distance = calculateDistance(origin,target);
	const azimuth = calculateAzimuth(origin,target);
	const elevation = calculateElevationAngle(origin.altitude,target.altitude,distance);
	
	const azimuthRad = MathUtils.degToRad(azimuth);
	const elevationRad = MathUtils.degToRad(elevation);
	
	const x = radius * Math.sin(azimuthRad);
	const y = radius * Math.sin(elevationRad);
	const z = radius * Math.cos(azimuthRad);
	
	return new Vector3(x,y,z);
}

function calculateDistance (origin,target) {
	const dLat = MathUtils.degToRad(target.lat - origin.lat);
	const dLon = MathUtils.degToRad(target.lng - origin.lng);
	const originLat = MathUtils.degToRad(origin.lat);
	const targetLat = MathUtils.degToRad(target.lat);
	
	const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(originLat) * Math.cos(targetLat) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
	const c = 2 * Math.atan2(Math.sqrt(a),Math.sqrt(1 - a));
	
	return EARTH_RADIUS * c;
}

function calculateAzimuth (origin,target) {
	const dLon = MathUtils.degToRad(target.lng - origin.lng);
	const originLat = MathUtils.degToRad(origin.lat);
	const targetLat = MathUtils.degToRad(target.lat);
	
	const y = Math.sin(dLon) * Math.cos(targetLat);
	const x = Math.cos(originLat) * Math.sin(targetLat) - Math.sin(originLat) * Math.cos(targetLat) * Math.cos(dLon);
	let azimuth = Math.atan2(y,x);
	azimuth = (MathUtils.radToDeg(azimuth) + 360) % 360;
	return azimuth;
}

function calculateElevationAngle (originHeight, targetHeight, distance) {
	const deltaH = targetHeight - originHeight;
	const elevationAngleRad = Math.atan(deltaH / distance);
	return MathUtils.radToDeg(elevationAngleRad);
}
